import {
  AppBar,
  Box,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { COLORS } from "../utils/colors";
import { trackStoreClick } from "../api/analytics";
import {
  GradientTypography,
  GradientButton,
  OutlineButton,
  StyledCard,
  FloatingBox,
  AppImage,
  HoverImage,
} from "./StyledComponents";

interface FeaturePhase {
  title: string;
  subtitle: string;
  items: string[];
}

interface Testimonial {
  quote: string;
  author: string;
}

interface LandingPageProps {
  // App info
  appName: string;
  appTagline: string;
  appDescription: string;
  logoPath: string;
  
  // Hero section
  heroTitle: string;
  heroHighlight: string;
  heroDescription: string;
  heroCta: string;
  heroSecondaryAction: string;
  
  // App screenshots
  screenshots: string[];
  
  // Features section
  featuresTitle: string;
  featuresHighlight: string;
  featuresDescription: string;
  phases: FeaturePhase[];
  
  // Testimonials
  testimonialsTitle: string;
  testimonials: Testimonial[];
  
  // Social proof
  socialProofTitle?: string;
  socialProofEmbeds?: string[];
  
  // Download section
  downloadTitle: string;
  downloadDescription: string;
  
  // Store links
  appStoreLink: string;
  playStoreLink: string;
  
  // Footer
  footerText: string;
}

const LandingPage = ({
  appName,
  appTagline,
  appDescription,
  logoPath,
  heroTitle,
  heroHighlight,
  heroDescription,
  heroCta,
  heroSecondaryAction,
  screenshots,
  featuresTitle,
  featuresHighlight,
  featuresDescription,
  phases,
  testimonialsTitle,
  testimonials,
  socialProofTitle,
  socialProofEmbeds,
  downloadTitle,
  downloadDescription,
  appStoreLink,
  playStoreLink,
  footerText,
}: LandingPageProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Handler for store image clicks
  const handleStoreClick = async (
    storeType: "app_store" | "play_store",
    source: string
  ) => {
    trackStoreClick(storeType, source);
  };

  return (
    <Box
      sx={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        background: "#ffffff",
        color: COLORS.dark,
        minHeight: "100vh",
      }}
    >
      {/* Navigation */}
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
        }}
      >
        <Toolbar
          sx={{
            maxWidth: { sm: "100%", md: "1200px" },
            width: "100%",
            mx: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <img
            src={logoPath}
            alt={`${appName} Logo`}
            style={{ height: 40, marginRight: 10 }}
          />

          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ flexGrow: 1, color: "black" }}
          >
            <GradientTypography variant="inherit">{appName}</GradientTypography>
          </Typography>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 4, mr: 4 }}>
              <Typography
                component="a"
                href="#how-it-works"
                sx={{
                  color: "text.primary",
                  textDecoration: "none",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                How It Works
              </Typography>
              <Typography
                component="a"
                href="#testimonials"
                sx={{
                  color: "text.primary",
                  textDecoration: "none",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                Memories
              </Typography>
            </Box>
          )}
          <GradientButton href="#download" disableElevation sx={{ mr: "40px" }}>
            Download App
          </GradientButton>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container
        component="section"
        maxWidth="md"
        sx={{
          pt: { xs: 16, sm: 20, md: 24 },
          pb: { xs: 8, md: 12 },
          textAlign: "center",
        }}
      >
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          fontWeight="bold"
          sx={{ mb: 3, lineHeight: 1.2 }}
        >
          {heroTitle}{" "}
          <GradientTypography variant="inherit">
            {heroHighlight}
          </GradientTypography>
        </Typography>
        <Typography
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ mb: 5, maxWidth: "85%", mx: "auto" }}
        >
          {heroDescription}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            gap: 2,
            mb: 6,
            width: "100%",
          }}
        >
          <GradientButton href="#download" size="large" disableElevation>
            {heroCta}
          </GradientButton>
          <OutlineButton href="#how-it-works" size="large" disableElevation>
            {heroSecondaryAction}
          </OutlineButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Host with the app. Guest with just a tap.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          <Link
            to="/early-access"
            onClick={() => handleStoreClick("app_store", "hero_section")}
          >
            <HoverImage src="appstore.webp" alt="App Store" height={48} />
          </Link>
          <Link
            to="/early-access"
            onClick={() => handleStoreClick("play_store", "hero_section")}
          >
            <HoverImage src="playstore.webp" alt="Google Play" height={48} />
          </Link>
        </Box>
      </Container>

      {/* App Screenshots */}
      <Box component="section" sx={{ bgcolor: "white" }}>
        <Container maxWidth="lg" sx={{ textAlign: "center" }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
          >
            {screenshots.map((screenshot, index) => (
              // @ts-expect-error
              <Grid
                item
                xs={12}
                md={5}
                key={index}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <FloatingBox sx={{ animationDelay: index % 2 ? "1s" : "0s" }}>
                  <AppImage
                    src={screenshot}
                    alt={`${appName} App Screenshot ${index + 1}`}
                  />
                </FloatingBox>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        id="how-it-works"
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(to bottom, white, #f5f5f5)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              fontWeight="bold"
              mb={2}
            >
              {featuresTitle}{" "}
              <GradientTypography variant="inherit">{featuresHighlight}</GradientTypography>
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: "800px", mx: "auto" }}
            >
              {featuresDescription}
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {phases.map((phase, index) => (
              // @ts-expect-error
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StyledCard>
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <GradientTypography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ display: "block", mb: 1 }}
                    >
                      {phase.title}
                    </GradientTypography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {phase.subtitle}
                    </Typography>
                    <List disablePadding>
                      {phase.items.map((item, i) => (
                        <ListItem
                          key={i}
                          disableGutters
                          sx={{ py: 0.5, justifyContent: "center" }}
                        >
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              variant: "body2",
                              color: "text.secondary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <Box
          id="testimonials"
          component="section"
          sx={{
            py: { xs: 8, md: 12 },
            background: "linear-gradient(to bottom, white, #f5f5f5)",
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant={isMobile ? "h4" : "h3"}
              fontWeight="bold"
              sx={{ mb: { xs: 4, md: 6 }, textAlign: "center" }}
            >
              {testimonialsTitle}
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {testimonials.map((t, i) => (
                // @ts-expect-error
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <StyledCard sx={{ p: 1 }}>
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        "{t.quote}"
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {t.author}
                      </Typography>
                    </Box>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Social Proof Section */}
      {socialProofEmbeds && socialProofEmbeds.length > 0 && (
        <Box
          component="section"
          sx={{
            py: { xs: 8, md: 12 },
            background: "linear-gradient(to bottom, #fff, #f5f5f5)",
            textAlign: "center",
          }}
        >
          <Container maxWidth="md">
            <Typography
              variant={isMobile ? "h4" : "h3"}
              fontWeight="bold"
              sx={{ mb: 4 }}
            >
              {socialProofTitle || `${appName} on Social Media`}
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {socialProofEmbeds.map((url, i) => (
                // @ts-expect-error
                <Grid item xs={12} sm={6} key={i}>
                  <Box
                    component="iframe"
                    src={url}
                    sx={{
                      width: "100%",
                      height: 600,
                      border: "none",
                      borderRadius: 2,
                      boxShadow: 3,
                    }}
                    allow="autoplay; encrypted-media"
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Download Section */}
      <Box
        id="download"
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          textAlign: "center",
          background: "linear-gradient(to bottom, #f5f5f5, white)",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            {downloadTitle}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 5 }}>
            {downloadDescription}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
            <Link
              to="/early-access"
              onClick={() =>
                handleStoreClick("app_store", "download_section")
              }
            >
              <HoverImage src="appstore.webp" alt="App Store" height={56} />
            </Link>
            <Link
              to="/early-access"
              onClick={() =>
                handleStoreClick("play_store", "download_section")
              }
            >
              <HoverImage src="playstore.webp" alt="Google Play" height={56} />
            </Link>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{ bgcolor: "grey.100", py: 3, textAlign: "center" }}
      >
        <Typography variant="body2" color="text.secondary">
          {footerText}
        </Typography>
      </Box>
    </Box>
  );
};

export default LandingPage;